import { ActivityType } from '@bioliberty/core';
import { Patient, SessionSummary } from '@bioliberty/lifemind';
import { Copy, Info, Sparkles } from 'lucide-react';
import posthog from 'posthog-js';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../../../components/Accordion';
import { Button } from '../../../components/Button';
import { Card, CardContent, CardHeader } from '../../../components/Card';
import { NoCapture } from '../../../components/NoCapture';
import { Tooltip, TooltipContent, TooltipTrigger } from '../../../components/Tooltip';
import { useToaster } from '../../../hooks/useToaster';
import { Event } from '../../../lib/posthog';
import { SessionActivityTable } from '../../../patients/views/details/SessionActivityTable';
import { getFeatureFlag } from '../../../stores/slices/feature-flags';
import { formatDate, formatSecondsString } from '../../../util/date';
import { MissingSessionNoteContent } from './MissingSessionNoteContent';
import { RegenerateSessionNoteButton } from './RegenerateSessionNoteButton';
import { SessionNoteRating } from './SessionNoteRating';

export interface RecentSessionsListItemProps {
  session: SessionSummary;
  patient: Patient;
  showActivities?: boolean;
}

export function RecentSessionsListItem({ session, patient, showActivities }: RecentSessionsListItemProps) {
  const { isEnabled: isComputerVisionEnabled } = useSelector(getFeatureFlag('computerVision'));
  const toast = useToaster();

  const filteredActivities = useMemo(
    () =>
      session.activities.filter(
        ({ activityTypeId }) => activityTypeId !== ActivityType.CV_MODE || isComputerVisionEnabled
      ),
    [session.activities, isComputerVisionEnabled]
  );

  const onCopyToClipboard = () => {
    navigator.clipboard.writeText(session.note ?? '');
    toast.success('Copied session summary to clipboard.');
    posthog.capture(Event.SessionNoteCopyButtonClick);
  };

  const defaultAccordion = session.note ? 'notes' : showActivities ? 'activities' : undefined;

  return (
    <li className="w-full">
      <Card className="w-full">
        <CardHeader className="flex flex-col justify-between pb-0 md:flex-row">
          <div>
            <Link to={`/patients/${patient.id}`}>
              <div className="line-clamp-1 font-bold">
                <NoCapture>{patient.alias}</NoCapture>
              </div>
            </Link>
            <div>{formatDate(new Date(session.startedOn))}</div>
          </div>

          <div className="flex flex-col gap-2 md:flex-row md:gap-16">
            <div>
              <div className="font-bold">Activities</div>
              <div>{filteredActivities.length}</div>
            </div>
            <div>
              <div className="font-bold">Duration</div>
              <div>{formatSecondsString(session.duration)}</div>
            </div>

            <div>
              <div className="font-bold">Total time on task</div>
              <div>{formatSecondsString(session.totalTimeTaken)}</div>
            </div>

            <div>
              <div className="font-bold">Total reps</div>
              <div>{session.totalReps}</div>
            </div>

            <div>
              <div className="font-bold">Avg RPE</div>
              <div>{session.avgFeedbackScore?.toFixed(2) ?? ''}</div>
            </div>
          </div>
        </CardHeader>
        <CardContent className="pb-0">
          <Accordion type="single" defaultValue={defaultAccordion} collapsible>
            <AccordionItem value="notes">
              <AccordionTrigger>
                <div className="flex items-center gap-1">
                  <div className="to-accent bg-linear-to-r from-green-500 bg-clip-text font-bold text-transparent">
                    AI Summary
                  </div>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Sparkles className="text-accent h-4 w-4" />
                    </TooltipTrigger>
                    <TooltipContent className="flex items-center gap-2">
                      <Info className="w-4" /> Uses AI. Please verify results.
                    </TooltipContent>
                  </Tooltip>
                </div>
              </AccordionTrigger>
              <AccordionContent>
                {!session.note ? (
                  <MissingSessionNoteContent session={session} />
                ) : (
                  <>
                    <div className="whitespace-pre-wrap">
                      <NoCapture>{session.note}</NoCapture>
                    </div>
                    <div className="mt-4 flex flex-col justify-between gap-2 md:flex-row md:items-center">
                      <div className="flex flex-col gap-2 md:flex-row">
                        <Button variant="accent" size="m" icon={Copy} onClick={onCopyToClipboard}>
                          Copy to clipboard
                        </Button>

                        <RegenerateSessionNoteButton session={session} />
                      </div>

                      <SessionNoteRating session={session} />
                    </div>
                  </>
                )}
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="activities">
              <AccordionTrigger className="font-bold">Activities ({filteredActivities.length})</AccordionTrigger>
              <AccordionContent>
                <SessionActivityTable activities={filteredActivities} />
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </CardContent>
      </Card>
    </li>
  );
}
